import { Box, Divider } from '@chakra-ui/layout';
import classNames from 'classnames';
import { useLayoutEffect, useRef } from 'react';

import { useResizeObserver } from '@revelio/core';

import { useGridLayout } from '../../hooks/useGridLayout';
import { Chart } from '../Chart/ChartRoot/Chart';
import { usePageExport } from '../Chart/chart-menu/PageExportProvider';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_HEIGHT,
} from '../PageConfigProvider/constants';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType, RevelioPageId } from '../PageConfigProvider/types';
import { ReportCoverPage } from '../Reports/ReportCoverPage';
import { ReportFooterPage } from '../Reports/ReportFooterPage';
import { ResizableSplitPane } from '../SplitPanel/ResizableSplitPane';
import { scrollPositionStore } from '../scroll-position-store';
import styles from './ContentGrid.module.css';
import { ReportWrapper } from './ReportWrapper';

export const ContentGrid = () => {
  const { config: pageConfig } = usePageConfig();
  const isReport = pageConfig.type === PageType.REPORT;
  const { config, revelioPageId } = useTabContentConfig();
  const { pdfExportRef } = usePageExport();
  const { items, grid, primaryFilter, title, description } = config;

  const gridRows = grid?.rows || DEFAULT_GRID_ROWS;
  const gridColumns = grid?.columns || DEFAULT_GRID_COLUMNS;
  const gridLayout = useGridLayout({
    isReport,
    gridRows,
    gridColumns,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    scrollPositionStore.restore(scrollContainerRef.current);
  }, []);

  const { height: scrollContainerHeight } =
    useResizeObserver(scrollContainerRef);

  const heightPct = (gridRows / DEFAULT_GRID_ROWS) * 100;
  const shouldOverflow =
    (gridRows && gridRows > DEFAULT_GRID_ROWS) || scrollContainerHeight < 560;

  const HeightBase = 100;
  const HeightPrimaryFilter = 42;

  const isTalentDiscovery =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY ||
    revelioPageId === RevelioPageId.POSTING_DISCOVERY;

  const heightDeduction = (() => {
    if (isReport) return 0;
    if (!primaryFilter) return HeightBase;
    return HeightBase + HeightPrimaryFilter;
  })();

  const numPages = Math.floor(gridRows / DEFAULT_GRID_ROWS);
  const pageDividers = Array.from({ length: numPages }).map((_, index) => (
    <Divider
      key={index}
      position="absolute"
      top={`${(index + 1) * REPORT_PAGE_HEIGHT}px`}
      left="0"
      right="0"
    />
  ));

  return (
    <Box
      ref={scrollContainerRef}
      className={styles.customScrollbar}
      position="relative"
      width="100%"
      height={
        isReport
          ? `calc(${heightPct}% - ${heightDeduction}px)`
          : `calc(100% - ${heightDeduction}px)`
      }
      onScroll={(e) => scrollPositionStore.save(e.currentTarget)}
      {...(shouldOverflow && { overflowY: 'auto', overflowX: 'hidden' })}
      {...(isReport && { backgroundColor: 'white', borderRadius: 'lg' })}
    >
      {isTalentDiscovery ? (
        <ResizableSplitPane items={items} />
      ) : (
        <ReportWrapper isReport={isReport}>
          <Box
            ref={pdfExportRef}
            display="grid"
            position="relative"
            gap={isReport ? '1rem' : 'var(--cg-gap, 1rem)'}
            width="100%"
            height={gridLayout.contentHeight}
            {...(isReport
              ? {
                  paddingLeft: gridLayout.styles.paddingLeft,
                  paddingRight: gridLayout.styles.paddingRight,
                }
              : { padding: 0 })}
            className={classNames(
              'content-grid',
              'print-root',
              !isReport && styles.dashboardGrid
            )}
            style={{
              gridTemplateRows: gridLayout.styles.gridTemplateRows,
              gridTemplateColumns: gridLayout.styles.gridTemplateColumns,
            }}
          >
            {isReport && (
              <>
                <Box
                  width="100%"
                  height={`${REPORT_PAGE_HEIGHT}px`}
                  position="absolute"
                >
                  <ReportCoverPage
                    title={title ?? ''}
                    description={description ?? ''}
                  />
                </Box>
                <Box
                  width="100%"
                  height={`${REPORT_PAGE_HEIGHT}px`}
                  position="absolute"
                  top={`${(numPages - 1) * REPORT_PAGE_HEIGHT}px`}
                >
                  <ReportFooterPage />
                </Box>
              </>
            )}
            {items.map((item) => {
              const { id, position, plot } = item;
              const itemStyle = {
                gridColumn: `${position.x + 1} / span ${position.width}`,
                gridRow: `${position.y + 1} / span ${position.height}`,
              };

              return (
                <Box
                  key={id}
                  style={itemStyle}
                  minWidth="0"
                  minHeight="0"
                  width="100%"
                  height="100%"
                  {...(isReport && plot?.color === '#ffffff' && { zIndex: 1 })}
                >
                  <Chart
                    item={item}
                    primaryFilter={primaryFilter}
                    {...(!isReport && {
                      headerSize:
                        position.height / gridRows <= 0.23 ? 'sm' : 'md',
                    })}
                    variant={isReport ? 'report' : 'default'}
                  />
                </Box>
              );
            })}
            {isReport && pageDividers}
          </Box>
        </ReportWrapper>
      )}
    </Box>
  );
};
